import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import {
  getPaginatedTickets,
  createTicketBuyer,
  createTicketSeller,
} from "../../services/ticketServices.ts";
import { X } from "lucide-react";
import { useToast } from "../../context/ToastContext.jsx";
import Pagination from "../common/Pagination";

const ProfileTickets = () => {
  const { user } = useAuth();
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";
  const { showSuccess, showError } = useToast();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [refreshData, setRefreshData] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [totalCounts, setTotalCounts] = useState({
    totalPending: 0,
    totalOpen: 0,
    totalResolved: 0,
  });
  const [showCreateTicketModal, setShowCreateTicketModal] = useState(false);
  const [formData, setFormData] = useState({
    subject: "",
  });
  const [statusFilter, setStatusFilter] = useState("");

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [search]);

  // Determine user type
  const isBuyer = user?.buyer?.id;
  const isSeller = user?.seller?.id;

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        setLoading(true);

        // Build query parameters for server-side pagination and filtering
        const params = {
          page: currentPage,
          limit: pageSize,
          orderBy: "id",
          order: "desc",
          filters: {
            userId: user?.id,
          },
        };

        // Add status filter parameter if provided
        if (statusFilter && statusFilter.trim() !== "") {
          params.filters.ticketStatusId = statusFilter.trim();
        }

        // Add search parameter if provided
        if (debouncedSearch && debouncedSearch.trim() !== "") {
          params.search = debouncedSearch.trim();
        }

        const response = await getPaginatedTickets(params);

        const ticketsData = response.data.data || response.data;

        setTickets(ticketsData);

        setTotalCounts({
          totalPending: response.totalPending,
          totalOpen: response.totalOpen,
          totalResolved: response.totalResolved,
        });

        // Update pagination state from API response
        if (response) {
          setPagination({
            currentPage: response.page,
            totalPages: response.pages,
            totalItems: response.total,
            itemsPerPage: pageSize,
          });
        } else {
          console.log("No pagination data in API response");
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching won vehicles:", error);
        setLoading(false);
        // Set empty array on error
        setTickets([]);
      }
    };
    fetchTickets();
  }, [currentPage, pageSize, refreshData, debouncedSearch, statusFilter]);

  const handleCreateTicketModalVisibility = (visibility) => {
    if (visibility) {
      setFormData({ subject: "" });
    }
    setShowCreateTicketModal(visibility);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    const formPayload = {
      subject: formData.subject,
    };

    try {
      let response;
      if (isBuyer) {
        response = await createTicketBuyer(formPayload);
      } else if (isSeller) {
        response = await createTicketSeller(formPayload);
      }
      setRefreshData(!refreshData);
      handleCreateTicketModalVisibility(false);
      showSuccess(translate("tickets.messages.ticketCreatedSuccessfully"));
    } catch (err) {
      console.error("Error submitting data:", err);
      showError(translate("tickets.messages.ticketCreationFailed"), err);
    } finally {
      setLoading(false);
    }
  };

  const getTicketStatusTag = (ticketStatus) => {
    if (!ticketStatus) return null;

    let color = "blue";
    let statusText = ticketStatus.name;

    switch (ticketStatus.slug) {
      case "pending":
        color = "orange";
        statusText = translate("tickets.statuses.pending");
        break;
      case "open":
        color = "blue";
        statusText = translate("tickets.statuses.open");
        break;
      case "resolved":
        color = "green";
        statusText = translate("tickets.statuses.resolved");
        break;
      case "closed":
        color = "gray";
        statusText = translate("tickets.statuses.closed");
        break;
      case "in-progress":
        color = "blue";
        statusText = translate("tickets.statuses.inProgress");
        break;
    }

    return (
      <span
        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-${color}-100 text-${color}-800`}
      >
        {statusText}
      </span>
    );
  };

  return (
    <>
      <div
        className='bg-[#FFF9F9] rounded-lg shadow-md'
        dir={isRTL ? "rtl" : "ltr"}
        lang={language}
      >
        <div className='bg-[#fcfcfc1f] text-gray-700 font-medium rounded-t-[16px] px-1 py-1'>
          <div className='p-[20px] text-lg rounded-t-[12px] font-semibold text-gray-700 bg-[#F153291F] border-b border-black pb-2'>
            <h2 className='text-base sm:text-lg font-semibold'>
              {translate("tickets.heading")}
            </h2>
          </div>
        </div>

        <div className='px-3 py-6'>
          {/* Top Stats */}
          <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-6'>
            <div className='bg-blue-50 p-4 rounded-lg border border-blue-200'>
              <div className='flex items-center'>
                <div className='p-2 bg-blue-100 rounded-full'>
                  <svg
                    className='w-6 h-6 text-blue-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
                    />
                  </svg>
                </div>
                <div className={`${isRTL ? "mr-3" : "ml-3"}`}>
                  <p className='text-sm font-medium text-blue-600'>
                    {translate("tickets.stats.openTickets")}
                  </p>
                  <p className='text-xl font-semibold text-blue-900'>
                    {totalCounts.totalOpen}
                  </p>
                </div>
              </div>
            </div>

            <div className='bg-green-50 p-4 rounded-lg border border-green-200'>
              <div className='flex items-center'>
                <div className='p-2 bg-green-100 rounded-full'>
                  <svg
                    className='w-6 h-6 text-green-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M5 13l4 4L19 7'
                    />
                  </svg>
                </div>
                <div className={`${isRTL ? "mr-3" : "ml-3"}`}>
                  <p className='text-sm font-medium text-green-600'>
                    {translate("tickets.stats.resolved")}
                  </p>
                  <p className='text-xl font-semibold text-green-900'>
                    {totalCounts.totalResolved}
                  </p>
                </div>
              </div>
            </div>

            <div className='bg-yellow-50 p-4 rounded-lg border border-yellow-200'>
              <div className='flex items-center'>
                <div className='p-2 bg-yellow-100 rounded-full'>
                  <svg
                    className='w-6 h-6 text-yellow-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                    />
                  </svg>
                </div>
                <div className={`${isRTL ? "mr-3" : "ml-3"}`}>
                  <p className='text-sm font-medium text-yellow-600'>
                    {translate("tickets.stats.pending")}
                  </p>
                  <p className='text-xl font-semibold text-yellow-900'>
                    {totalCounts.totalPending}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filter */}
          <div className='bg-gray-50 rounded-lg p-4 mb-6'>
            <div className='flex flex-col md:flex-row gap-4'>
              <input
                type='text'
                placeholder={translate("tickets.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                  isRTL ? "text-right" : ""
                }`}
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={`p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                  isRTL ? "text-right" : ""
                }`}
              >
                <option value=''>{translate("tickets.allStatus")}</option>
                <option value='1'>
                  {translate("tickets.statuses.pending")}
                </option>
                <option value='2'>{translate("tickets.statuses.open")}</option>
                <option value='3'>
                  {translate("tickets.statuses.resolved")}
                </option>
              </select>
              <button
                onClick={() => {
                  handleCreateTicketModalVisibility(true);
                }}
                className='bg-orange-500 text-white px-6 py-2 rounded-md hover:bg-orange-600'
              >
                {translate("tickets.newTicket")}
              </button>
            </div>
          </div>

          {/* Tickets Table */}
          <div className='bg-white rounded-lg shadow overflow-hidden'>
            <table className='w-full'>
              <thead className='bg-gray-50'>
                <tr>
                  <th
                    className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      isRTL ? "text-right" : "text-left"
                    }`}
                  >
                    {translate("tickets.ticketNumber")}
                  </th>
                  <th
                    className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      isRTL ? "text-right" : "text-left"
                    }`}
                  >
                    {translate("tickets.subject")}
                  </th>
                  <th
                    className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      isRTL ? "text-right" : "text-left"
                    }`}
                  >
                    {translate("tickets.status")}
                  </th>
                  <th
                    className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      isRTL ? "text-right" : "text-left"
                    }`}
                  >
                    {translate("tickets.created")}
                  </th>
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-gray-200'>
                {tickets.length == 0 ? (
                  <div className='text-center py-12'>
                    <div className='text-gray-400 mb-4'>
                      <svg
                        className='mx-auto h-12 w-12'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M5 13l4 4L19 7'
                        />
                      </svg>
                    </div>
                    <h3 className='text-lg font-medium text-gray-900 mb-2'>
                      {translate("tickets.messages.noTicketsFound")}
                    </h3>
                    <p className='text-gray-500'>
                      {translate("tickets.messages.noTicketsYet")}
                    </p>
                  </div>
                ) : (
                  tickets.map((ticket) => (
                    <tr>
                      <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900'>
                        {ticket.ticketNumber}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {ticket.subject}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        {getTicketStatusTag(ticket?.ticketStatus)}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                        {new Date(ticket?.createdAt).toLocaleDateString(
                          language === "ar" ? "ar-AE" : "en-US",
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className='px-6 py-4 border-t border-gray-200'>
                <Pagination
                  totalPages={pagination.totalPages}
                  currentPage={pagination.currentPage}
                  setCurrentPage={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {showCreateTicketModal == true && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto'>
            {/* Header */}
            <div className='flex items-center justify-between p-6 border-b'>
              <h3 className='text-lg font-semibold text-gray-900'>
                {translate("tickets.modal.createTicket")}
              </h3>
              <button
                onClick={() => {
                  handleCreateTicketModalVisibility(false);
                }}
                className='text-gray-400 hover:text-gray-600 transition-colors'
              >
                <X className='w-6 h-6' />
              </button>
            </div>

            {/* Content */}
            <div className='p-6'>
              <form onSubmit={handleSubmit}>
                <textarea
                  placeholder={translate("tickets.modal.subjectPlaceholder")}
                  name='subject'
                  id='subject'
                  value={formData.subject}
                  onChange={(e) =>
                    setFormData({ ...formData, subject: e.target.value })
                  }
                  required
                  className={`w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 placeholder:text-sm ${
                    isRTL ? "text-right" : ""
                  }`}
                ></textarea>
                <br />
                <button
                  type='submit'
                  className='w-full bg-blue-500 text-white rounded py-2 text-center hover:bg-blue-600'
                >
                  {translate("tickets.modal.submit")}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfileTickets;
